import { useState, useRef } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Search, UserRound, Plus, X } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"

type PrimaryImage = { url: string; alt_text: string }

type PersonRow = {
  id: number
  name: string
  slug: string
  members_count: number
  recordings_count: number
  gigs_count: number
  in_amh: boolean
  primary_image: PrimaryImage | null
}

type Props = {
  people: PersonRow[]
  total_count: number
  filtered_count: number | null
  search: string | null
  can_create: boolean
}

function PersonCard({ person }: { person: PersonRow }) {
  return (
    <Link
      href={`/people/${person.slug}`}
      className="group block w-full text-left bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
    >
      <div className="aspect-square bg-stone-100 dark:bg-stone-800 relative overflow-hidden">
        {person.primary_image ? (
          <img
            src={person.primary_image.url}
            alt={person.primary_image.alt_text}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <UserRound className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
          </div>
        )}
        {person.in_amh && (
          <span className="absolute top-2 left-2 text-xs font-medium text-amber-700 dark:text-amber-300 bg-white/90 dark:bg-stone-900/90 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full leading-none">
            AMH
          </span>
        )}
      </div>

      <div className="p-3">
        <h3
          className="text-sm font-semibold text-stone-900 dark:text-white mb-1.5 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {person.name}
        </h3>
        <div
          className="flex items-center gap-1.5 text-xs text-stone-400 dark:text-stone-500"
          style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
        >
          <span>{person.members_count}b</span>
          <span className="text-stone-300 dark:text-stone-700">·</span>
          <span>{person.recordings_count}r</span>
          {person.gigs_count > 0 && (
            <>
              <span className="text-stone-300 dark:text-stone-700">·</span>
              <span>{person.gigs_count}g</span>
            </>
          )}
        </div>
      </div>
    </Link>
  )
}

function statusText(people: PersonRow[], total_count: number, filtered_count: number | null, search: string | null): string {
  if (search) {
    const count = filtered_count ?? people.length
    if (people.length < count) return `Showing 100 of ${count.toLocaleString()} people matching "${search}"`
    return `${count.toLocaleString()} ${count !== 1 ? "people" : "person"} matching "${search}"`
  }
  return `Search to browse all ${total_count.toLocaleString()} People`
}

export default function PeopleIndex({ people, total_count, filtered_count, search, can_create }: Props) {
  const [query, setQuery] = useState(search ?? "")
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  function handleQueryChange(value: string) {
    setQuery(value)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      router.get(
        "/people",
        value.trim() ? { search: value.trim() } : {},
        { preserveState: true, preserveScroll: true }
      )
    }, 300)
  }

  function clearSearch() {
    setQuery("")
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    router.get("/people", {}, { preserveState: true, preserveScroll: true })
  }

  const status = statusText(people, total_count, filtered_count, search ?? null)

  return (
    <>
      <Head title="People">
        <meta name="description" content="Browse all Australian musicians and artists in the Australian Music Database." />
        <meta property="og:title" content="People" />
        <meta property="og:description" content="Browse all Australian musicians and artists in the Australian Music Database." />
      </Head>

      <PublicShell>
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-bold text-stone-900 dark:text-white mb-1 tracking-tight"
              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
            >
              People
            </h1>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {status}
            </p>
          </div>
          {can_create && (
            <Link
              href="/people/new"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors no-underline shrink-0"
            >
              <Plus className="w-4 h-4" />
              New person
            </Link>
          )}
        </div>

        <div className="relative mb-8 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
          <input
            type="search"
            placeholder="Search people…"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-sm text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400 dark:focus:border-amber-500 transition-colors"
          />
          {query && (
            <button
              onClick={clearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {people.length === 0 ? (
          <div className="py-20 text-center">
            <UserRound className="w-7 h-7 text-stone-300 dark:text-stone-700 mx-auto mb-3" />
            <p className="text-sm text-stone-400 dark:text-stone-500">
              No people found for &ldquo;{query}&rdquo;
            </p>
            <button
              onClick={clearSearch}
              className="mt-3 text-sm text-amber-600 dark:text-amber-400 hover:underline"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))" }}>
            {people.map((person) => (
              <PersonCard key={person.id} person={person} />
            ))}
          </div>
        )}
      </PublicShell>
    </>
  )
}
